import { Link, useParams } from '@tanstack/react-router';
import { useTranslation } from '@/context/TranslationContext';
import { useBlogPost } from '@/lib/queries/blog';
import { SEO, generateArticleSchema, generateBreadcrumbSchema } from '@/components/shared/seo';
import styles from './BlogDetail.module.scss';

export function BlogDetail() {
    const { slug } = useParams({ from: '/blog/$slug' as any });
    const { language, t } = useTranslation();
    const { data: post, isLoading: loading } = useBlogPost(slug as string);
    const notFound = !loading && !post;

    // Helper to get ISO date string
    const getISODate = (date: any): string => {
        if (!date) return new Date().toISOString();
        try {
            let d: Date;
            if (date.toDate) {
                d = date.toDate();
            } else if (date._seconds) {
                d = new Date(date._seconds * 1000);
            } else if (typeof date === 'string' || typeof date === 'number') {
                d = new Date(date);
            } else {
                d = date;
            }
            return d.toISOString();
        } catch {
            return new Date().toISOString();
        }
    };

    const formatDate = (date: any) => {
        if (!date) return '';

        try {
            let d: Date;

            // Handle different date formats from Firebase
            if (date.toDate) {
                // Firebase Timestamp object
                d = date.toDate();
            } else if (date._seconds) {
                // Firebase Timestamp JSON format
                d = new Date(date._seconds * 1000);
            } else if (typeof date === 'string' || typeof date === 'number') {
                // String or number timestamp
                d = new Date(date);
            } else {
                // Already a Date object
                d = date;
            }

            // Validate the date is valid
            if (isNaN(d.getTime())) {
                return '';
            }

            return new Intl.DateTimeFormat(language === 'de' ? 'de-DE' : 'en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }).format(d);
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    if (loading) {
        return (
            <div className={styles.blogDetailContainer}>
                <div className={styles.loading}>
                    <div className={styles.spinner}></div>
                    <p>{t('BLOG.POST_LOADING')}</p>
                </div>
            </div>
        );
    }

    if (notFound || !post) {
        return (
            <div className={styles.blogDetailContainer}>
                <SEO
                    title={t('BLOG.POST_NOT_FOUND')}
                    description={t('BLOG.POST_NOT_FOUND_TEXT')}
                    canonicalUrl={`/blog/${slug}`}
                    language={language}
                    noIndex={true}
                />
                <div className={styles.notFound}>
                    <i className='bx bx-error-circle'></i>
                    <h2>{t('BLOG.POST_NOT_FOUND')}</h2>
                    <p>{t('BLOG.POST_NOT_FOUND_TEXT')}</p>
                    <Link to="/blog" params={{} as any} className={styles.backButton}>
                        <i className='bx bx-left-arrow-alt'></i>
                        {t('BLOG.BACK_TO_BLOG')}
                    </Link>
                </div>
            </div>
        );
    }

    // Generate structured data for blog post
    const postTitle = language === 'en' && post.titleEn ? post.titleEn : post.title;
    const postExcerpt = language === 'en' && post.excerptEn ? post.excerptEn : post.excerpt;
    const postTags = language === 'en' && post.tagsEn ? post.tagsEn : post.tags;
    const publishedDate = getISODate(post.date);

    const articleSchema = generateArticleSchema({
        title: postTitle,
        description: postExcerpt,
        slug: post.slug,
        image: post.imageUrl,
        publishedDate,
        author: post.author,
        tags: postTags,
    });

    const breadcrumbSchema = generateBreadcrumbSchema([
        { name: language === 'de' ? 'Startseite' : 'Home', url: '/' },
        { name: 'Blog', url: '/blog' },
        { name: postTitle, url: `/blog/${post.slug}` },
    ]);

    const combinedSchema = {
        '@context': 'https://schema.org',
        '@graph': [articleSchema, breadcrumbSchema],
    };

    return (
        <div className={styles.blogDetailContainer}>
            <SEO
                title={postTitle}
                description={postExcerpt}
                keywords={postTags?.join(', ')}
                canonicalUrl={`/blog/${post.slug}`}
                ogType="article"
                ogImage={post.imageUrl}
                ogImageAlt={postTitle}
                language={language}
                publishedTime={publishedDate}
                author={post.author}
                section="Technology"
                structuredData={combinedSchema}
            />
            <article className={styles.blogPost}>
                <header className={styles.postHeader}>
                    <Link to="/blog" params={{} as any} className={styles.backLink}>
                        <i className='bx bx-left-arrow-alt'></i>
                        {t('BLOG.BACK_TO_BLOG')}
                    </Link>

                    <div className={styles.postMeta} data-aos="fade-up">
                        <span>
                            <i className='bx bx-calendar'></i>
                            {formatDate(post.date)}
                        </span>
                        <span>
                            <i className='bx bx-user'></i>
                            {post.author}
                        </span>
                    </div>

                    <h1 data-aos="fade-up" data-aos-delay="100">
                        {language === 'en' && post.titleEn ? post.titleEn : post.title}
                    </h1>

                    <div className={styles.postTags} data-aos="fade-up" data-aos-delay="200">
                        {(language === 'en' && post.tagsEn ? post.tagsEn : post.tags).map((tag, i) => (
                            <span key={i} className={styles.tag}>{tag}</span>
                        ))}
                    </div>
                </header>

                <div className={styles.postImage} data-aos="fade-up" data-aos-delay="300">
                    <img src={post.imageUrl} alt={language === 'en' && post.titleEn ? post.titleEn : post.title} />
                </div>

                <div className={styles.postContent} data-aos="fade-up" data-aos-delay="400">
                    <div
                        dangerouslySetInnerHTML={{ __html: language === 'en' && post.contentEn ? post.contentEn : post.content }}
                        className={styles.markdownContent}
                    ></div>
                </div>



                <footer className={styles.postFooter}>
                    <Link to="/blog" params={{} as any} className={styles.backButton}>
                        <i className='bx bx-left-arrow-alt'></i>
                        {t('BLOG.BACK_TO_BLOG')}
                    </Link>
                </footer>
            </article >
        </div >
    );
}
